import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'

import { Subject } from 'rxjs'
import { map, share, switchMap } from 'rxjs/operators'

import { environment } from '@env'
import { FormMode } from '@core/form/form.enums'
import { LazyParams } from '@shared/components/table/table.models'
import { SortingService } from '@core/services/sorting/sorting.service'
import { MatchesService } from '@core/services/matches/matches.service'

import { MarketablePackageSearchResult } from './marketable.models'
import { UserCustomerViewService } from '../user-customers/user-customer-view.service'
import { PermissionManagementService } from '@features/permission-management/permission-management.service'
import { logError } from '@shared/opretators/log-error.operator'
import { getSales } from '@core/utils/sales.utils'
import { mapOptions } from '@core/utils/options.utils'
import { groupingOptions, toArray } from './marketable.utils'

@Injectable()
export class MarketableService {
  readonly #http = inject(HttpClient)
  readonly #sortingService = inject(SortingService)
  readonly #matchesService = inject(MatchesService)
  readonly #userCustomerViewService = inject(UserCustomerViewService)
  readonly #permissionManagementService = inject(PermissionManagementService)

  newSale$ = new Subject<void>()
  newMarketablePackage$ = new Subject<void>()

  getSales$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    return getSales(this.#http, this.#sortingService, params, lazyParams)
  }

  newUpdateSale$(sale, type = FormMode.Edit) {
    const isEdit = type === FormMode.Edit
    sale = isEdit ? [sale] : sale
    return this.#http[isEdit ? 'put' : 'post'](environment.API.CRUD_SALE, sale)
  }

  newUpdateMarketablePackage(marketablePackage, type = FormMode.Edit) {
    const isEdit = type === FormMode.Edit
    marketablePackage = isEdit ? [marketablePackage] : marketablePackage
    return this.#http[isEdit ? 'put' : 'post'](
      environment.API.CRUD_MARKETABLE_PACKAGE,
      marketablePackage,
    )
  }

  getMarketablePackages$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    // The Filters component reports a resolved-but-empty async option list as [] ; this
    // endpoint expects seasonId/competitionId as a single optional Guid, so an empty
    // selection must be sent as null or the backend rejects the payload (400)
    const filtering: Record<string, any> = {
      freeText: '',
      filterQuality: 'ListWithItems',
      ...params,
    }
    ;['seasonId', 'competitionId'].forEach((key) => {
      const value = filtering[key]
      if (Array.isArray(value)) {
        filtering[key] = value.length ? value[0] : null
      }
    })
    const paramsBackEnd = {
      filtering,
      ...lazyParams,
    }
    return this.#http
      .post<{
        marketablePackages: MarketablePackageSearchResult[]
        [key: string]: any
      }>(environment.API.SEARCH_MARKETABLE_PACKAGES, paramsBackEnd)
      .pipe(
        map((data) => ({
          ...data,
          paging: {
            totalRecords: data.marketablePackages.length,
            ...data['paging'],
          },
          sorting: {
            ...data['sorting'],
            ...lazyParams.sorting,
          },
          marketablePackages: this.#sortingService.sort(data.marketablePackages),
        })),
        share(),
        logError(),
      )
  }

  getMarketablePackagesGroupOptions$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    return this.getMarketablePackages$(params, lazyParams).pipe(
      map(({ marketablePackages }) => {
        return marketablePackages.reduce(groupingOptions, {})
      }),
      map(toArray),
    )
  }

  getMatchGroupOptions$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    return this.#matchesService.getMatchesPublishing$(params, lazyParams).pipe(
      map(({ matches }) => {
        return matches.reduce(groupingOptions, {})
      }),
      map(toArray),
    )
  }

  getMarketablePackageById(id: string) {
    return this.#http
      .get<any>(`${environment.API.CRUD_MARKETABLE_PACKAGE}s/${id}`)
      .pipe(share(), logError())
  }

  getMarketablePackageWithSalesById$(id: string) {
    return this.getMarketablePackageById(id).pipe(
      switchMap((marketablePackage) =>
        this.getSales$({ marketablePackageIds: [id] }).pipe(
          map(({ sales }) => ({ ...marketablePackage, associatedSales: sales })),
        ),
      ),
    )
  }

  getCustomerOptions$() {
    return this.#userCustomerViewService.getCustomerOptions$()
  }

  getSeasonOptions$(forceFetch?: boolean) {
    return this.#matchesService.getSeasonOptions$(forceFetch)
  }

  getCompetitionsBySeasonOptions$(seasonId: string, forceFetch?: boolean) {
    return this.#matchesService.getCompetitionsBySeasonOptions$(
      seasonId,
      false,
      undefined,
      forceFetch,
    )
  }

  getCategoryGroupOptions$() {
    return this.#permissionManagementService.getCategoryGroupOptions$(true)
  }

  fetchTeams$(params = {}) {
    return this.#http
      .post<{
        teams: any[]
        [key: string]: any
      }>(environment.API.SEARCH_TEAMS, params)
      .pipe(
        map((data) => mapOptions(data.teams)),
        share(),
        logError(),
      )
  }
}
